import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Cookiebanner en voorkeurenpaneel (snippets/cookie-consent.liquid).
 *
 * De toestemming zelf staat bij Shopify: het thema leest en schrijft haar
 * via de Customer Privacy API, zodat Shopify's eigen pixels, de Web Pixels
 * van apps en de checkout dezelfde keuze zien. Er is geen eigen cookie of
 * localStorage.
 *
 * De banner verschijnt alleen als Shopify zegt dat het moet
 * (`shouldShowBanner`: een bezoeker in een regio waar toestemming nodig is)
 * en er nog geen keuze is. `?cookiebanner` in de URL toont hem altijd, om te
 * testen. Het paneel is een theme-drawer; links naar `#cookie-instellingen`
 * (de footer) openen het, ook als de banner weg is.
 */

/** @type {ConsentCategory[]} */
const CATEGORIES = ['preferences', 'marketing', 'analytics'];
const SETTINGS_LINK = 'a[href$="#cookie-instellingen"], a[href*="ConsentBanner"]';

/** @type {Promise<CustomerPrivacy | null> | undefined} */
let privacyApi;

/** @returns {Promise<CustomerPrivacy | null>} */
function loadPrivacyApi() {
  privacyApi ??= new Promise((resolve) => {
    if (window.Shopify?.customerPrivacy) return resolve(window.Shopify.customerPrivacy);
    if (!window.Shopify?.loadFeatures) return resolve(null);

    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], (error) => {
      resolve(error ? null : window.Shopify.customerPrivacy ?? null);
    });
  });

  return privacyApi;
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement} banner
 *
 * @extends {Component<Refs>}
 */
class CookieConsentComponent extends Component {
  requiredRefs = ['banner'];

  /** Of de bezoeker nog moet kiezen: dan komt de banner terug als het paneel zonder keuze dichtgaat. */
  #pending = false;

  #bannerObserver = new ResizeObserver(() => this.#reserveBannerSpace());

  connectedCallback() {
    super.connectedCallback();

    this.#bannerObserver.observe(this.refs.banner);
    document.addEventListener('click', this.#onSettingsLinkClick);
    this.#drawer?.addEventListener('theme-drawer:close', this.#onDrawerClose);

    loadPrivacyApi().then((privacy) => {
      const forced = new URLSearchParams(window.location.search).has('cookiebanner');
      const consent = privacy?.currentVisitorConsent();
      const undecided = CATEGORIES.every((category) => !consent?.[category]);

      if (forced || (privacy?.shouldShowBanner() && undecided)) {
        this.#pending = true;
        this.#showBanner();
      }

      if (window.location.hash === '#cookie-instellingen') this.openPreferences();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#bannerObserver.disconnect();
    document.removeEventListener('click', this.#onSettingsLinkClick);
    this.#drawer?.removeEventListener('theme-drawer:close', this.#onDrawerClose);
  }

  /** @returns {import('./theme-drawer').ThemeDrawer | null} */
  get #drawer() {
    return this.querySelector('theme-drawer');
  }

  /** @returns {HTMLInputElement[]} */
  get #switches() {
    return Array.from(this.querySelectorAll('input[data-consent]'));
  }

  async openPreferences() {
    this.#hideBanner();

    const consent = (await loadPrivacyApi())?.currentVisitorConsent();
    for (const input of this.#switches) {
      input.checked = consent?.[/** @type {ConsentCategory} */ (input.dataset.consent)] === 'yes';
    }

    this.#drawer?.open();
  }

  acceptAll() {
    this.#record(true);
  }

  rejectAll() {
    this.#record(false);
  }

  save() {
    this.#record();
  }

  /**
   * Zet de schakelaars (bij alles aan of alles uit) en legt ze vast.
   *
   * @param {boolean} [all] - Alles aan of alles uit; zonder waarde de stand van de schakelaars.
   */
  async #record(all) {
    /** @type {Partial<Record<ConsentCategory, boolean>>} */
    const consent = {};
    for (const input of this.#switches) {
      if (all !== undefined) input.checked = all;
      consent[/** @type {ConsentCategory} */ (input.dataset.consent)] = input.checked;
    }

    this.#pending = false;
    this.#hideBanner();
    this.#drawer?.close();

    (await loadPrivacyApi())?.setTrackingConsent(consent);
  }

  #showBanner() {
    const { banner } = this.refs;

    // Komt het paneel dicht terwijl de banner nog wegschuift: die animatie afbreken.
    banner.classList.remove('cookie-banner--closing');
    banner.hidden = false;
  }

  #hideBanner() {
    const { banner } = this.refs;
    if (banner.hidden) return;

    banner.classList.add('cookie-banner--closing');
    onAnimationEnd(banner, () => {
      if (!banner.classList.contains('cookie-banner--closing')) return;

      banner.hidden = true;
      banner.classList.remove('cookie-banner--closing');
    });
  }

  /**
   * Zolang de banner er staat krijgt de pagina onderaan zijn hoogte erbij, zodat
   * de footer (met de juridische links) erboven kan scrollen.
   */
  #reserveBannerSpace() {
    const { banner } = this.refs;
    const root = document.documentElement;

    if (banner.hidden) {
      root.style.removeProperty('--cookie-banner-space');
      return;
    }

    const space = banner.offsetHeight + parseFloat(getComputedStyle(banner).bottom) + 16;
    root.style.setProperty('--cookie-banner-space', `${space}px`);
  }

  /** @param {MouseEvent} event */
  #onSettingsLinkClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest(SETTINGS_LINK)) return;

    event.preventDefault();
    this.openPreferences();
  };

  #onDrawerClose = () => {
    if (this.#pending) this.#showBanner();
  };
}

if (!customElements.get('cookie-consent-component')) {
  customElements.define('cookie-consent-component', CookieConsentComponent);
}
